import React, { createContext, useContext, useState, useEffect } from 'react'
import { useOrganization } from './OrganizationContext'
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures'
import { supabase } from '../lib/supabase'

interface WizardData {
  // Step 1: Organization Profile
  organizationProfile: {
    businessName: string
    businessType: 'ice_rink' | 'training_center' | 'sports_complex' | 'dance_martial_arts' | 'other'
    businessTypeOther?: string
    phone: string
    email: string
    address: string
    city: string
    state: string
    zipCode: string
    timezone: string
    businessHours: {
      [key: string]: { open: string; close: string; closed: boolean }
    }
  }
  
  // Step 2: Branding
  branding: {
    logo?: File | null
    logoUrl?: string
    logoFile?: File | null
    primaryColor: string
    backgroundColor: string
    accentColor: string
    textColor: string
    darkMode: boolean
  }
  
  // Step 3: Facilities
  facilities: Array<{
    id: string
    name: string
    capacity: number
    description?: string
    amenities?: string[]
  }>
  
  // Step 4: Services
  services: Array<{
    id: string
    name: string
    duration: number
    price: number
    facilityIds: string[]
    capacity?: number
    requiresWaiver: boolean
    description?: string
    category?: string
    maxParticipants?: number
    isHourlyRental?: boolean
    peakHourlyRate?: number
    offPeakHourlyRate?: number
    minRentalDuration?: number
    memberPrice?: number
    nonMemberPrice?: number
    ageMin?: number | null
    ageMax?: number | null
    skillLevel?: string | null
  }>
  
  // Step 5: Packages
  packages: Array<{
    id: string
    name: string
    type: 'session_based' | 'time_based' | 'custom'
    price: number
    description?: string
    credits?: number
    validityDays?: number
    applicableServices?: string[]
    duration?: number
    includes?: string[]
    maxPurchasesPerCustomer?: number | null
    requiresMembership?: boolean
    isActive?: boolean
  }>
  
  // Step 6: Memberships
  memberships: Array<{
    id: string
    name: string
    price: number
    period: 'monthly' | 'annual' | 'seasonal'
    bookingsIncluded: number
    description?: string
    benefits: string[]
  }>
  
  // Step 7: Schedule Templates
  scheduleTemplates: Array<{
    id: string
    name: string
    serviceId: string
    recurrence: 'weekly' | 'biweekly' | 'monthly'
    instances: Array<{
      dayOfWeek: number
      startTime: string
      endTime: string
    }>
    isActive?: boolean
  }>
  
  // Step 8: Team
  teamMembers: Array<{
    id: string
    name: string
    email: string
    role: 'admin' | 'staff'
  }>
}

interface SetupWizardContextType {
  currentStep: number
  totalSteps: number
  wizardData: WizardData
  isLoading: boolean
  canProgress: boolean
  
  // Navigation
  goToStep: (step: number) => void
  nextStep: () => void
  previousStep: () => void
  
  // Data management
  updateStepData: <K extends keyof WizardData>(step: K, data: Partial<WizardData[K]>) => void
  saveProgress: () => Promise<void>
  completeSetup: () => Promise<void>
  
  // Validation
  validateCurrentStep: () => boolean
  getStepErrors: (step: number) => string[]
}

const defaultWizardData: WizardData = {
  organizationProfile: {
    businessName: '',
    businessType: 'ice_rink',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    businessHours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '09:00', close: '17:00', closed: false },
      sunday: { open: '09:00', close: '17:00', closed: true },
    }
  },
  branding: {
    logo: null,
    primaryColor: '#9CA3AF',
    backgroundColor: '#000000',
    accentColor: '#9CA3AF',
    textColor: '#ffffff',
    darkMode: true
  },
  facilities: [],
  services: [],
  packages: [],
  memberships: [],
  scheduleTemplates: [],
  teamMembers: []
}

const SetupWizardContext = createContext<SetupWizardContextType | undefined>(undefined)

export function SetupWizardProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useOrganization()
  const { isTrainerMode } = useSubscriptionFeatures()
  const [currentStep, setCurrentStep] = useState(0)
  const [wizardData, setWizardData] = useState<WizardData>(defaultWizardData)
  const [isLoading, setIsLoading] = useState(false)
  
  const totalSteps = 10 // Including welcome and review steps (added packages, schedules)

  // Load saved progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('setupWizardProgress')
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress)
        setWizardData(parsed.data || defaultWizardData)
        setCurrentStep(parsed.step || 0)
      } catch (error) {
        // Failed to load wizard progress, continue with default data
      }
    }
    
    // Pre-fill organization data if available
    if (organization) {
      setWizardData(prev => ({
        ...prev,
        organizationProfile: {
          ...prev.organizationProfile,
          businessName: organization.name || prev.organizationProfile.businessName,
          email: organization.settings?.email?.replyTo || prev.organizationProfile.email,
          phone: organization.settings?.phone || prev.organizationProfile.phone,
        },
        branding: {
          ...prev.branding,
          primaryColor: organization.settings?.primaryColor || prev.branding.primaryColor,
          backgroundColor: organization.settings?.backgroundColor || prev.branding.backgroundColor,
          accentColor: organization.settings?.accentColor || prev.branding.accentColor,
          textColor: organization.settings?.textColor || prev.branding.textColor,
          darkMode: organization.settings?.darkMode ?? prev.branding.darkMode,
        }
      }))
    }
  }, [organization])

  // Save progress to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('setupWizardProgress', JSON.stringify({
      step: currentStep,
      data: wizardData,
      timestamp: new Date().toISOString()
    }))
  }, [currentStep, wizardData])

  const updateStepData = <K extends keyof WizardData>(
    step: K,
    data: Partial<WizardData[K]>
  ) => {
    setWizardData(prev => ({
      ...prev,
      [step]: { ...prev[step], ...data }
    }))
  }

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 0: // Welcome
        return true
        
      case 1: // Organization Profile
        const profile = wizardData.organizationProfile
        return !!(
          profile.businessName &&
          profile.businessType &&
          profile.phone &&
          profile.email &&
          profile.address &&
          profile.city &&
          profile.state &&
          profile.zipCode
        )
        
      case 2: // Branding
        return true // All branding fields are optional
        
      case 3: // Facilities
        return wizardData.facilities.length > 0
        
      case 4: // Services
        return wizardData.services.length > 0
        
      case 5: // Packages
        return true // Packages are optional
        
      case 6: // Memberships
        return true // Memberships are optional
        
      case 7: // Schedule Templates
        return true // Schedule templates are optional
        
      case 8: // Team
        return true // Team members are optional
        
      case 9: // Review
        return true
        
      default:
        return false
    }
  }

  const getStepErrors = (step: number): string[] => {
    const errors: string[] = []
    
    switch (step) {
      case 1: // Organization Profile
        const profile = wizardData.organizationProfile
        if (!profile.businessName) errors.push('Business name is required')
        if (!profile.phone) errors.push('Phone number is required')
        if (!profile.email) errors.push('Email is required')
        if (!profile.address) errors.push('Address is required')
        if (!profile.city) errors.push('City is required')
        if (!profile.state) errors.push('State is required')
        if (!profile.zipCode) errors.push('ZIP code is required')
        break
        
      case 3: // Facilities
        if (wizardData.facilities.length === 0) {
          errors.push('At least one facility is required')
        }
        break
        
      case 4: // Services
        if (wizardData.services.length === 0) {
          errors.push('At least one service is required')
        }
        break
    }
    
    return errors
  }

  const goToStep = (step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step)
    }
  }

  const nextStep = () => {
    if (currentStep < totalSteps - 1 && validateCurrentStep()) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const saveProgress = async () => {
    // This is already being saved to localStorage automatically
    // In the future, we could save to the database here
  }

  const completeSetup = async () => {
    setIsLoading(true)
    try {
      // Get organization ID from context
      const { currentOrganization } = organization
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      // Upload logo if provided
      let logoUrl = wizardData.branding.logoUrl || null
      if (wizardData.branding.logoFile) {
        try {
          const file = wizardData.branding.logoFile
          const fileExt = file.name.split('.').pop()
          const fileName = `${currentOrganization.id}/logo.${fileExt}`
          const filePath = `organization-logos/${fileName}`

          // Upload to Supabase storage
          const { error: uploadError } = await supabase.storage
            .from('public')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true
            })

          if (uploadError) {
            // Logo upload failed, continue without logo
          } else {
            // Get public URL
            const { data: { publicUrl } } = supabase.storage
              .from('public')
              .getPublicUrl(filePath)
            logoUrl = publicUrl
          }
        } catch (error) {
          // Continue without logo if upload fails
        }
      }

      // Prepare data for the RPC call
      const setupData = {
        p_organization_id: currentOrganization.id,
        p_branding: {
          logo_url: logoUrl,
          primary_color: wizardData.branding.primaryColor,
          background_color: wizardData.branding.backgroundColor,
          accent_color: wizardData.branding.accentColor || wizardData.branding.primaryColor,
          text_color: wizardData.branding.textColor || (wizardData.branding.darkMode ? '#ffffff' : '#1a1a1a'),
          dark_mode: wizardData.branding.darkMode
        },
        p_facilities: wizardData.facilities.map(f => ({
          name: f.name,
          capacity: f.capacity,
          description: f.description || '',
          amenities: f.amenities || []
        })),
        p_services: wizardData.services.map(s => ({
          name: s.name,
          duration: s.duration,
          price: s.price,
          description: s.description || ''
        })),
        p_memberships: wizardData.memberships.map(m => ({
          name: m.name,
          price: m.price,
          period: m.period || 'monthly',
          description: m.description || '',
          benefits: m.benefits || []
        })),
        p_team_members: wizardData.teamMembers.map(t => ({
          name: t.name,
          email: t.email,
          role: t.role
        }))
      }

      // Call the RPC function
      const { data, error } = await supabase
        .rpc('complete_setup_wizard', setupData)

      if (error) throw error

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to complete setup')
      }

      // Auto-create default facility
      // For trainer mode, use "Coach Studio" as the default facility name
      const defaultFacilityName = isTrainerMode ? 'Coach Studio' : 'My Programs'
      const { error: facilityError } = await supabase
        .from('facilities')
        .insert({
          organization_id: currentOrganization.id,
          name: defaultFacilityName,
          is_active: true,
          is_default: true,
          capacity: null
        })

      // Facility creation is optional, silently continue if it fails
      if (facilityError) {
        // Continue - facility creation failure is not critical to setup completion
      }

      // Update organization settings in context
      await organization.updateSettings({
        branding: setupData.p_branding,
        business: {
          ...organization.currentOrganization.settings?.business,
          name: wizardData.organizationProfile.businessName,
          phone: wizardData.organizationProfile.phone,
          email: wizardData.organizationProfile.email,
          address: wizardData.organizationProfile.address,
          city: wizardData.organizationProfile.city,
          state: wizardData.organizationProfile.state,
          zip_code: wizardData.organizationProfile.zipCode,
          timezone: wizardData.organizationProfile.timezone,
          business_hours: wizardData.organizationProfile.businessHours
        }
      })

      // Clear localStorage
      localStorage.removeItem('setupWizardProgress')

      // Redirect to dashboard
      window.location.href = '/dashboard'
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const value: SetupWizardContextType = {
    currentStep,
    totalSteps,
    wizardData,
    isLoading,
    canProgress: validateCurrentStep(),
    goToStep,
    nextStep,
    previousStep,
    updateStepData,
    saveProgress,
    completeSetup,
    validateCurrentStep,
    getStepErrors
  }

  return (
    <SetupWizardContext.Provider value={value}>
      {children}
    </SetupWizardContext.Provider>
  )
}

export function useSetupWizard() {
  const context = useContext(SetupWizardContext)
  if (!context) {
    throw new Error('useSetupWizard must be used within a SetupWizardProvider')
  }
  return context
}